import React from 'react';
import { Chart as ReactChartJSLineChart } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js";
import { Timeseries } from "types/analytics";
import { ProgressSpinner } from "primereact/progressspinner";
import styled from "styled-components";
import moment from "moment";
import { usePlatforms } from 'components/NewRelease/hooks/usePlatforms';
import { getRandomColor } from 'components/Analytics/utils';

interface LineChartProps {
  timeseries?: Timeseries[] | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

const verticalLinePlugin = {
  id: "verticalLine",
  afterDraw: (chart: any) => {
    const tooltip = chart.tooltip;
    if (!tooltip?.getActiveElements) return;
    const activeElements = tooltip.getActiveElements();
    if (!activeElements || activeElements.length === 0) return;

    const canvas = chart.canvas;
    const mouseY = chart._lastEvent?.y ?? null;
    if (mouseY == null) return;

    let closest = activeElements[0];
    let minDist = Infinity;
    activeElements.forEach((el: any) => {
      const y = el.element.y;
      const dist = Math.abs(mouseY - y);
      if (dist < minDist) {
        minDist = dist;
        closest = el;
      }
    });

    const x = closest.element.x;
    const topY = chart.scales.y?.top;
    const bottomY = chart.scales.y?.bottom;

    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, topY);
    ctx.lineTo(x, bottomY);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#00000033";
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(verticalLinePlugin);

export const LineChart: React.FC<LineChartProps> = ({ timeseries, from, to }) => {
  const { platforms, fetching } = usePlatforms();
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<any>(null);

  const filteredTimeseries = React.useMemo(() => {
    if (!from || !to) return timeseries;
    const fromDate = moment(from);
    const toDate = moment(to);
    return timeseries?.filter(item => {
      const date = moment(item.date);
      return date.isBetween(fromDate, toDate, "day", "[]");
    });
  }, [timeseries, from, to]);

  const platformsMap: Record<string, string> = React.useMemo(() => {
    return platforms.reduce((acc, platform) => {
      acc[String(platform.id)] = platform.name;
      return acc;
    }, {} as Record<string, string>);
  }, [platforms]);

  const labels: string[] = React.useMemo(() => {
    if (from && to) {
      const start = moment(from);
      const end = moment(to);
      const range: string[] = [];
      for (let d = start.clone(); d.isSameOrBefore(end, "day"); d.add(1, "day")) {
        range.push(d.format("YYYY-MM-DD"));
      }
      return range;
    }
    const lastWeek: string[] = [];
    for (let i = 6; i >= 0; i--) {
      lastWeek.push(moment().subtract(i, "days").format("YYYY-MM-DD"));
    }
    return lastWeek;
  }, [from, to]);

  const datasets = React.useMemo(() => {
    const data: Record<string, Record<string, number>> = (filteredTimeseries || []).reduce((acc, item) => {
      const key = String(item.platform_id);
      if (!acc[key]) acc[key] = {};
      acc[key][item.date] = item.plays;
      return acc;
    }, {} as Record<string, Record<string, number>>);

    const result = Object.entries(data).map(([key, values]) => {
      const color = getRandomColor() || 'rgb(255, 99, 132)';
      return {
        type: 'line' as const,
        borderWidth: 2,
        borderColor: color,
        backgroundColor: color,
        fill: false,
        data: labels.map(label => values[label] || 0),
        name: platformsMap[key] || '-',
        getCurrentDateValue: (date: string) => values[date] || 0,
      };
    });

    return result.length ? result : [{ type: 'line' as const, fill: false, data: [] }];
  }, [filteredTimeseries, labels, platformsMap]);

  React.useEffect(() => {
    const chart = chartRef?.current?.chart || chartRef?.current;
    if (!chart) return;
    const canvas = chart.canvas;
    if (!canvas) return;

    const handleMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const elements = chart.getElementsAtEventForMode(e, "index", { intersect: false }, false);
      if (!elements || elements.length === 0) {
        chart.tooltip?.setActiveElements?.([], { x, y });
        chart.update("none");
        return;
      }

      let closest = elements[0];
      let minDist = Infinity;
      elements.forEach((el: any) => {
        const elY = el.element?.y ?? el.y;
        const dist = Math.abs(elY - y);
        if (dist < minDist) {
          minDist = dist;
          closest = el;
        }
      });

      const active = [{ datasetIndex: closest.datasetIndex, index: closest.index }];
      chart.tooltip?.setActiveElements?.(active, { x, y });
      chart.setActiveElements?.(active);
      chart.update("none");
    };

    canvas.addEventListener("mousemove", handleMove);
    return () => canvas.removeEventListener("mousemove", handleMove);
  }, [datasets, labels]);

  const maxPlays = Math.max(...(timeseries?.map(t => t.plays) || []), 1000);

  if (fetching) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <ReactChartJSLineChart
        ref={chartRef}
        type="line"
        data={{ labels, datasets }}
        options={{
          interaction: { mode: "index", intersect: false },
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            y: { beginAtZero: true, min: 0, max: maxPlays * 1.1, ticks: { stepSize: Math.ceil(maxPlays / 4) } },
          },
          plugins: {
            tooltip: {
              enabled: false,
              external: (context: any) => {
                const tooltipEl = tooltipRef.current;
                if (!tooltipEl) return;
                const tooltipModel = context.tooltip;
                if (tooltipModel.opacity === 0) {
                  tooltipEl.style.opacity = "0";
                  return;
                }

                const date = tooltipModel.title?.[0] ?? "";
                let title = `<div class="title">${date}</div>`;
                let innerHtml = "";
                let point = tooltipModel.dataPoints?.[0] ?? null;

                if (tooltipModel.dataPoints?.length) {
                  const mouseY = context.chart._lastEvent?.y ?? null;
                  if (mouseY != null) {
                    let minDist = Infinity;
                    tooltipModel.dataPoints.forEach((p: any) => {
                      const y = p.element.y;
                      const dist = Math.abs(mouseY - y);
                      if (dist < minDist) {
                        minDist = dist;
                        point = p;
                      }
                    });
                  }
                }

                const hoveredPointName = point ? point.dataset.name : null;
                datasets.forEach((item: any) => {
                  innerHtml += `
                    <div class="row" style="opacity: ${item.name === hoveredPointName ? 1 : 0.4}">
                      <div class="info">
                        <span class="dot" style="background: ${item.backgroundColor}"></span>
                        <span class="label">${item.name}</span>
                      </div>
                      <span class="value" style="color: ${item.backgroundColor}">
                        ${item.getCurrentDateValue(date)}
                      </span>
                    </div>`;
                });

                tooltipEl.innerHTML = `<div>${title}<div class="content">${innerHtml}</div></div>`;
                const { offsetLeft, offsetTop } = context.chart.canvas;
                tooltipEl.style.opacity = "1";
                tooltipEl.style.left = offsetLeft + tooltipModel.caretX + "px";
                tooltipEl.style.top = offsetTop + tooltipModel.caretY + "px";
              },
            },
            legend: { display: false },
          },
        }}
      />
      <Tooltip ref={tooltipRef} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 321px;
  background: rgba(246, 246, 246, 1);
  border-radius: 10px;
  padding: 37px 16px 22px 20px;
`;

const Tooltip = styled.div`
  width: 210px;
  display: flex;
  flex-direction: column;
  position: absolute;
  opacity: 0;
  pointer-events: none;
  transform: translate(10px, -100%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px 3px #BCBCBFAD;
  white-space: nowrap;

  .title {
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color: #00000080;
    padding: 8px 12px;
    border-bottom: 1px solid #00000033;
  }

  .content {
    padding: 8px 12px;
    display: flex;
    gap: 15px;
    flex-direction: column;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    opacity: 0.4;
  }

  .info {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
  }

  .label, .value {
    font-size: 13px;
    line-height: 15px;
  }
`;
